import { c as _c } from "react/compiler-runtime";
import AttachmentTile from '@precis-digital/kurama/AttachmentTile';
import Grid from '@precis-digital/kurama/Grid';
import DatatableIcon from '@precis-digital/kurama/icons/DatatableIcon';
import ImageIcon from '@precis-digital/kurama/icons/ImageIcon';
import { styled } from '@precis-digital/kurama/utils';
import { type ReactElement } from 'react';
import { useCopilot } from 'shared/context/CopilotContext';
import { useTranslation } from 'shared/translations';

// Kurama's `theme.spacing` indexes a fixed scale ([4, 8, 12, 16, …]px) rather
// than multiplying a base unit, so only whole indices are valid: spacing(1) is
// 8px, and a fractional index silently yields an invalid value.
/** Lets the layout test assert the row's real computed gap. */
export const ATTACHMENT_ROW_TEST_ID = 'copilot-composer-attachment-row';
const StyledTileRow = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(1)
}));

/**
 * The attachment row below the Copilot composer input: one square tile per
 * attached image plus the CSV tile.
 *
 * Both kinds share one row because that is how a user thinks about "what is
 * attached to this message", even though CSV and images take different routes
 * to the agent. A no-op on the drawer, where both features are disabled and the
 * context fields stay at their idle defaults.
 *
 * A validation rejection (wrong type, too large, empty, too many, …) never
 * creates a tile — see `useCopilotCsvAttachment`'s `uploadAndAttachFile` and
 * `useCopilotImageAttachments`'s `admitFiles` — so it has nothing here to
 * attach to; `CopilotContext` surfaces it as a toast instead. An image tile
 * reaches `error` only when its own upload fails, so that tile carries the
 * upload-failure tooltip itself. The hook-level `imageAttachmentErrorReason`
 * must not drive it: retrying one tile, or a later validation rejection,
 * clears or replaces that shared reason while other tiles are still in
 * `error`.
 */
export const CopilotComposerAttachments = () => {
  const $ = _c(34);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "copilot.composer"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("common", t0);
  const {
    attachedCsvFile,
    pendingCsvFilename,
    csvAttachmentStatus,
    csvAttachmentErrorReason,
    clearCsvAttachment,
    dismissCsvAttachmentError,
    retryCsvAttachment,
    imageAttachments,
    removeImageAttachment,
    retryImageAttachment
  } = useCopilot();
  const csvFilename = attachedCsvFile?.filename ?? pendingCsvFilename;
  const hasCsvTile = csvAttachmentStatus !== "idle" || attachedCsvFile != null;
  let t1;
  if ($[1] !== t) {
    t1 = t("attachmentErrorLabel");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const errorLabel = t1;
  let t2;
  if ($[3] !== csvAttachmentErrorReason || $[4] !== t) {
    t2 = csvAttachmentErrorReason === "uploadFailed" ? t("csvAttachment.errors.uploadFailed") : undefined;
    $[3] = csvAttachmentErrorReason;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const csvErrorTooltip = t2;
  let t3;
  if ($[6] !== t) {
    t3 = t("imageAttachment.errors.uploadFailed");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const imageUploadFailedTooltip = t3;
  if (imageAttachments.length === 0 && !hasCsvTile) {
    return null;
  }
  let t4;
  if ($[8] !== clearCsvAttachment || $[9] !== csvAttachmentStatus || $[10] !== csvErrorTooltip || $[11] !== csvFilename || $[12] !== dismissCsvAttachmentError || $[13] !== errorLabel || $[14] !== hasCsvTile || $[15] !== retryCsvAttachment || $[16] !== t) {
    t4 = hasCsvTile ? <AttachmentTile icon={<DatatableIcon />} filename={csvFilename ?? ""} status={csvAttachmentStatus === "idle" ? "success" : csvAttachmentStatus} onRemove={csvAttachmentStatus === "error" ? dismissCsvAttachmentError : clearCsvAttachment} onRetry={csvAttachmentStatus === "error" ? () => {
      retryCsvAttachment();
    } : undefined} removeAriaLabel={t("csvAttachment.removeAria")} retryAriaLabel={t("csvAttachment.retryAria")} errorLabel={errorLabel} errorTooltip={csvErrorTooltip} /> : null;
    $[8] = clearCsvAttachment;
    $[9] = csvAttachmentStatus;
    $[10] = csvErrorTooltip;
    $[11] = csvFilename;
    $[12] = dismissCsvAttachmentError;
    $[13] = errorLabel;
    $[14] = hasCsvTile;
    $[15] = retryCsvAttachment;
    $[16] = t;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== errorLabel || $[19] !== imageAttachments || $[20] !== imageUploadFailedTooltip || $[21] !== removeImageAttachment || $[22] !== retryImageAttachment || $[23] !== t) {
    let t6;
    if ($[25] !== errorLabel || $[26] !== imageUploadFailedTooltip || $[27] !== removeImageAttachment || $[28] !== retryImageAttachment || $[29] !== t) {
      t6 = attachment => <AttachmentTile key={attachment.id} icon={<ImageIcon />} filename={attachment.filename} previewUrl={attachment.previewUrl} status={attachment.status} onRemove={() => {
        removeImageAttachment(attachment.id);
      }} onRetry={() => {
        retryImageAttachment(attachment.id);
      }} removeAriaLabel={t("imageAttachment.removeAria")} retryAriaLabel={t("imageAttachment.retryAria")} errorLabel={errorLabel} errorTooltip={attachment.status === "error" ? imageUploadFailedTooltip : undefined} />;
      $[25] = errorLabel;
      $[26] = imageUploadFailedTooltip;
      $[27] = removeImageAttachment;
      $[28] = retryImageAttachment;
      $[29] = t;
      $[30] = t6;
    } else {
      t6 = $[30];
    }
    t5 = imageAttachments.map(t6);
    $[18] = errorLabel;
    $[19] = imageAttachments;
    $[20] = imageUploadFailedTooltip;
    $[21] = removeImageAttachment;
    $[22] = retryImageAttachment;
    $[23] = t;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[31] !== t4 || $[32] !== t5) {
    t6 = <Grid><StyledTileRow data-testid={ATTACHMENT_ROW_TEST_ID}>{t4}{t5}</StyledTileRow></Grid>;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  return t6;
};